<template>
  <div id="formas-pagamento-modal-app">
    <modal
      elem-id="modal_crud_formas_pagamento"
      size="small"
      :close-label="t('Fechar')"
      :save-label="t('Salvar')"
      :save-disabled-fn="() => !formRef?.canSave"
      :save-fn="handleSave"
      :title="modalTitle"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal"
    >
      <!-- Content only renders when modal is visible (prevents API calls on mount) -->
      <template v-if="isVisible">
        <!-- Action Buttons -->
        <div class="text-right" style="margin-bottom: 20px;" v-if="isEditMode">
        <Button
          :label="t('Excluir')"
          icon="pi pi-trash"
          severity="danger"
          data-testid="formas-pagamento-btn-excluir"
          @click="handleDelete" />
      </div>

        <!-- Form -->
        <formas-pagamento-form
          ref="formRef"
          :is-edit-mode="isEditMode"
          @save-success="handleSaveSuccess"
          @delete-success="handleDeleteSuccess">
        </formas-pagamento-form>
      </template>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import FormasPagamentoForm from './FormasPagamentoForm.vue';
import { eventBus } from '../../eventBus';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { log } from '../../utils/DebugLogger';

type FormaPagamentoId = number | string;

interface FormasPagamentoFormInstance {
  canSave: boolean;
  initializeForNew: () => void | Promise<void>;
  loadForEdit: (id: FormaPagamentoId) => Promise<void>;
  saveFormaPagamento: () => Promise<void>;
  deleteFormaPagamento: () => Promise<void>;
  resetForm: () => void;
}

// Get i18n composable
const { t } = useI18n();
const { confirmAction } = useConfirmAction();

// Define emits
const emit = defineEmits<{
  close: [];
}>();

// Reactive data
const isVisible = ref<boolean>(false);
const isEditMode = ref<boolean>(false);
const formRef = ref<FormasPagamentoFormInstance | null>(null);
const currentFormaPagamentoId = ref<FormaPagamentoId>(0);

// Computed properties
const modalTitle = computed<string>(() => {
  return isEditMode.value ? t('Editar Forma de Pagamento') : t('Nova Forma de Pagamento');
});

// Methods
const openForNew = async (): Promise<void> => {
  log('FormasPagamentoModal', 'openForNew', 'Opening new payment method modal');
  isVisible.value = true;
  isEditMode.value = false;
  currentFormaPagamentoId.value = 0;

  // Wait for the form component to be rendered
  await nextTick();

  // Initialize form for new payment method
  if (formRef.value) {
    await formRef.value.initializeForNew();
  }
};

const openForEdit = async (formaPagamentoId?: FormaPagamentoId): Promise<void> => {
  log('FormasPagamentoModal', 'openForEdit', 'Opening edit modal for ID', formaPagamentoId);
  if (formaPagamentoId == null) return;
  isVisible.value = true;
  isEditMode.value = true;
  currentFormaPagamentoId.value = formaPagamentoId;

  // Wait for the form component to be rendered
  await nextTick();

  // Load payment method data in form
  if (formRef.value) {
    await formRef.value.loadForEdit(formaPagamentoId);
  }
};

const closeModal = (): void => {
  log('FormasPagamentoModal', 'closeModal', 'Closing modal');
  isVisible.value = false;
  isEditMode.value = false;
  currentFormaPagamentoId.value = 0;

  // Reset form
  if (formRef.value) {
    formRef.value.resetForm();
  }

  // Notify parent to hide modal (unmount via v-if)
  emit('close');
};

const handleSave = async (): Promise<void> => {
  log('FormasPagamentoModal', 'handleSave', 'Delegating save to form');
  if (formRef.value) {
    await formRef.value.saveFormaPagamento();
  }
};

const handleDelete = (): void => {
  log('FormasPagamentoModal', 'handleDelete', 'Delegating delete to form');

  confirmAction({
    message: t('Tem certeza que deseja excluir esta forma de pagamento?'),
    header: t('Confirmação'),
    onAccept: async () => {
      if (formRef.value) {
        await formRef.value.deleteFormaPagamento();
      }
    }
  });
};

const handleSaveSuccess = (): void => {
  log('FormasPagamentoModal', 'handleSaveSuccess', 'Save success, closing modal');
  closeModal();
};

const handleDeleteSuccess = (): void => {
  log('FormasPagamentoModal', 'handleDeleteSuccess', 'Delete success, closing modal');
  closeModal();
};

interface ModalOpenPayload {
  id?: FormaPagamentoId;
}

// Standard modal open handler (for modal.formas-pagamento.open event)
const handleModalOpen = async (data?: ModalOpenPayload): Promise<void> => {
  log('FormasPagamentoModal', 'handleModalOpen', 'Standard modal open event received', data);

  if (data?.id) {
    // Edit mode
    await openForEdit(data.id);
  } else {
    // New mode
    await openForNew();
  }
};

// Lifecycle
onMounted(() => {
  log('FormasPagamentoModal', 'mounted', 'Component mounted');

  // Standard modal events (preferred pattern)
  eventBus.on('modal.formas-pagamento.open', handleModalOpen);
  eventBus.on('modal.formas-pagamento.close', closeModal);

  // Legacy events (for backward compatibility)
  eventBus.on('formas-pagamento.edit', openForEdit);
});

onBeforeUnmount(() => {
  log('FormasPagamentoModal', 'beforeUnmount', 'Cleaning up event listeners');

  // Remove standard modal events
  eventBus.off('modal.formas-pagamento.open', handleModalOpen);
  eventBus.off('modal.formas-pagamento.close', closeModal);

  // Remove legacy events
  eventBus.off('formas-pagamento.edit', openForEdit);
});

// Expose methods for parent components
defineExpose({
  openForNew,
  openForEdit,
  closeModal
});
</script>

<style scoped>
.text-right {
  text-align: right;
}
</style>
